import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2, Package, Pencil, Ban, Globe } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const huFmt = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 2 });
const formatFt = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : `${huFmt.format(n)} Ft`;

function emptyItem() {
  return {
    kind: "product",
    product_id: "",
    product_name: "",
    unit: "",
    quantity: "1",
    net_price: "0",
    vat_rate: "27",
  };
}

export default function SupplierOrderFormDialog({
  open,
  onOpenChange,
  initial,            // existing order object (for edit) or null
  onCreated,
  onUpdated,
}) {
  const isEdit = !!initial?._id;
  const [supplierId, setSupplierId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [sentToEmail, setSentToEmail] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [suppliers, setSuppliers] = useState([]);
  const [sites, setSites] = useState([]);
  const [products, setProducts] = useState([]);
  const [supplierPrices, setSupplierPrices] = useState([]); // for chosen supplier
  const [saving, setSaving] = useState(false);

  // Reset when dialog opens
  useEffect(() => {
    if (!open) return;
    if (initial?._id) {
      setSupplierId(initial.supplier_id || "");
      setSiteId(initial.site_id || "");
      setSentToEmail(initial.sent_to_email || "");
      setNote(initial.note || "");
      setItems(
        (initial.items || []).map((it) => ({
          kind: it.kind || "product",
          product_id: it.product_id || "",
          product_name: it.product_name || "",
          unit: it.unit || "",
          quantity: String(it.quantity ?? ""),
          net_price: String(it.net_price ?? ""),
          vat_rate: String(it.vat_rate ?? ""),
        }))
      );
    } else {
      setSupplierId("");
      setSiteId("");
      setSentToEmail("");
      setNote("");
      setItems([emptyItem()]);
    }
  }, [open, initial]);

  // Load suppliers + sites + all products once
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [s, st, p] = await Promise.all([
          api.get("/partners", { params: { type: "supplier" } }),
          api.get("/sites"),
          api.get("/supplier-products"),
        ]);
        setSuppliers(s.data || []);
        setSites(st.data || []);
        setProducts(p.data || []);
      } catch (e) {
        toast.error(
          formatApiError(e?.response?.data?.detail) ||
            "Nem sikerült betölteni a törzsadatokat."
        );
      }
    })();
  }, [open]);

  // When supplier changes, load that supplier's price list so we can
  // pre-fill prices when selecting a product.
  useEffect(() => {
    if (!supplierId) {
      setSupplierPrices([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/partners/${supplierId}/prices`);
        setSupplierPrices(data || []);
      } catch {
        setSupplierPrices([]);
      }
    })();
    // Auto-fill default order email when supplier changes (prefer
    // dedicated order_email, fallback to first contact email).
    if (!isEdit) {
      const sup = suppliers.find((s) => s._id === supplierId);
      if (sup) {
        if ((sup.order_email || "").trim()) {
          setSentToEmail(sup.order_email.trim());
        } else {
          const first = (sup.contacts || []).find((c) => c.email);
          if (first?.email) setSentToEmail(first.email);
        }
      }
    }
  }, [supplierId]);

  const selectedSupplier = useMemo(
    () => suppliers.find((s) => s._id === supplierId) || null,
    [suppliers, supplierId]
  );
  const supplierOrderEmail = (selectedSupplier?.order_email || "").trim();
  const supplierBlocksOrder = !!selectedSupplier && !supplierOrderEmail;

  const totals = useMemo(() => {
    let net = 0;
    let gross = 0;
    for (const it of items) {
      const qty = Number(it.quantity) || 0;
      const price = Number(it.net_price) || 0;
      const vat = Number(it.vat_rate) || 0;
      const lineNet = qty * price;
      net += lineNet;
      gross += lineNet * (1 + vat / 100);
    }
    return { net: Math.round(net * 100) / 100, gross: Math.round(gross * 100) / 100 };
  }, [items]);

  const updateItem = useCallback((idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }, []);

  const removeItem = useCallback((idx) => {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  }, []);

  const onPickProduct = (idx, productId) => {
    if (!productId) {
      updateItem(idx, { product_id: "", product_name: "", unit: "" });
      return;
    }
    const prod = products.find((p) => p._id === productId);
    if (!prod) return;
    // Try to find an existing supplier price for this product
    const priceRow = supplierPrices.find((sp) => sp.product_id === productId);
    updateItem(idx, {
      product_id: productId,
      product_name: prod.name,
      unit: prod.unit || "",
      ...(priceRow
        ? {
            net_price: String(priceRow.net_price ?? "0"),
            vat_rate: String(priceRow.vat_rate ?? "27"),
          }
        : {}),
    });
  };

  const validate = () => {
    if (!supplierId) return "Beszállító kiválasztása kötelező.";
    if (supplierBlocksOrder) {
      return (
        "Ez a partner csak a saját weboldalán fogad rendeléseket. " +
        "Vegyél fel a partnerhez egy rendelési e-mail címet, majd indítsd újra."
      );
    }
    if (!siteId) return "Telephely kiválasztása kötelező.";
    if (!items.length) return "Legalább egy tétel szükséges.";
    for (const it of items) {
      if (!(it.product_name || "").trim()) return "Minden tételhez kötelező megnevezés.";
      if (it.kind === "product" && !it.product_id) {
        return "Termékes tételhez kötelező a termék kiválasztása (vagy válts 'Szabad tételre').";
      }
      if (!(Number(it.quantity) > 0)) return "Minden tételhez pozitív mennyiség szükséges.";
      if (Number(it.net_price) < 0) return "A nettó ár nem lehet negatív.";
    }
    return null;
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      const body = {
        supplier_id: supplierId,
        site_id: siteId,
        sent_to_email: sentToEmail.trim() || null,
        note: note.trim() || null,
        items: items.map((it) => ({
          kind: it.kind,
          product_id: it.kind === "product" ? it.product_id : null,
          product_name: it.product_name.trim(),
          unit: (it.unit || "").trim() || null,
          quantity: Number(it.quantity),
          net_price: Number(it.net_price),
          vat_rate: Number(it.vat_rate),
        })),
      };
      let resp;
      if (isEdit) {
        resp = await api.patch(`/supplier-orders/${initial._id}`, body);
        toast.success("Rendelés frissítve.");
        onUpdated?.(resp.data);
      } else {
        resp = await api.post("/supplier-orders", body);
        toast.success("Rendelés vázlat létrehozva.");
        onCreated?.(resp.data);
      }
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült menteni a rendelést."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="supplier-order-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Rendelés szerkesztése" : "Új beszállítói rendelés"}
          </DialogTitle>
          <DialogDescription>
            Vázlat státuszú rendelést hozol létre. Az e-mailes küldés és a
            visszavonás külön gombokkal érhető el a részletes nézetben.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Top row: supplier + site */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="supplier">Beszállító *</Label>
              <Select value={supplierId} onValueChange={setSupplierId} disabled={isEdit}>
                <SelectTrigger id="supplier" data-testid="supplier-select">
                  <SelectValue placeholder="Válassz beszállítót…" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                      Nincs beszállító. Vegyél fel egyet a Partnerek modulban.
                    </div>
                  ) : (
                    suppliers.map((s) => (
                      <SelectItem key={s._id} value={s._id}>
                        {s.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {isEdit && (
                <p className="text-[11px] text-muted-foreground">
                  A beszállító elküldés után nem módosítható.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="site">Szállítási telephely *</Label>
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger id="site" data-testid="site-select">
                  <SelectValue placeholder="Válassz telephelyet…" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {supplierBlocksOrder && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-500/30 dark:bg-amber-500/10"
              data-testid="supplier-blocks-order"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                  <Ban className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-amber-900 dark:text-amber-200">
                    Ez a partner csak a saját weboldalán fogad rendeléseket.
                  </p>
                  <p className="mt-1 text-[12px] leading-relaxed text-amber-800/90 dark:text-amber-200/80">
                    A(z) <b>{selectedSupplier?.name}</b> partnernél nincs rögzítve
                    {" "}&bdquo;rendelési e-mail cím&rdquo;. Amíg ez nincs megadva, csak a beszállító
                    saját webshop / portál felületén keresztül indítható rendelés.
                  </p>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-amber-800/90 dark:text-amber-200/80">
                    <Globe className="h-3.5 w-3.5" />
                    Lépj a Partnerek modulba → szerkeszd a partnert → add meg a
                    {" "}&bdquo;Rendelési e-mail cím&rdquo; mezőt, és indítsd újra a rendelést.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!supplierBlocksOrder && (
          <>
          <div className="space-y-1.5">
            <Label htmlFor="email">Beszállító e-mail címe (küldéshez)</Label>
            <Input
              id="email"
              type="email"
              placeholder="pl. rendeles@beszallito.hu"
              value={sentToEmail}
              onChange={(e) => setSentToEmail(e.target.value)}
              data-testid="sent-to-email"
            />
            <p className="text-[11px] text-muted-foreground">
              {supplierOrderEmail
                ? `A partner alapértelmezett rendelési címe: ${supplierOrderEmail}`
                : "Üresen hagyhatod most, küldéskor is megadható."}
            </p>
          </div>

          {/* Items */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Tételek *</Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setItems((prev) => [...prev, emptyItem()])}
                data-testid="add-item"
              >
                <Plus className="h-3.5 w-3.5" /> Tétel hozzáadása
              </Button>
            </div>

            <div className="space-y-3">
              {items.map((it, idx) => (
                <ItemRow
                  key={idx}
                  index={idx}
                  item={it}
                  products={products}
                  onPickProduct={(pid) => onPickProduct(idx, pid)}
                  onChange={(patch) => updateItem(idx, patch)}
                  onRemove={() => removeItem(idx)}
                  removable={items.length > 1}
                />
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="space-y-1.5">
            <Label htmlFor="note">Megjegyzés</Label>
            <Textarea
              id="note"
              rows={3}
              placeholder="Pl. szállítási idő, megjegyzés a beszállítónak…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="order-note"
            />
          </div>

          {/* Totals */}
          <div className="rounded-lg border border-border bg-muted/20 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Nettó összesen</span>
              <span className="tabular-nums font-medium">{formatFt(totals.net)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-base">
              <span className="font-semibold">Bruttó összesen</span>
              <span
                className="tabular-nums font-bold"
                data-testid="total-gross"
              >
                {formatFt(totals.gross)}
              </span>
            </div>
          </div>
          </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Mégse
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving || supplierBlocksOrder}
            data-testid="save-order"
          >
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {isEdit ? "Mentés" : "Vázlat létrehozása"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemRow({ index, item, products, onPickProduct, onChange, onRemove, removable }) {
  const isFree = item.kind === "free";
  const lineNet = (Number(item.quantity) || 0) * (Number(item.net_price) || 0);

  return (
    <div
      className="rounded-lg border border-border bg-background p-3"
      data-testid={`item-row-${index}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            variant={isFree ? "ghost" : "secondary"}
            className="h-7 px-2.5"
            onClick={() => onChange({ kind: "product" })}
            data-testid={`item-${index}-kind-product`}
          >
            <Package className="mr-1 h-3.5 w-3.5" /> Termékből
          </Button>
          <Button
            type="button"
            size="sm"
            variant={isFree ? "secondary" : "ghost"}
            className="h-7 px-2.5"
            onClick={() => onChange({ kind: "free", product_id: "" })}
            data-testid={`item-${index}-kind-free`}
          >
            <Pencil className="mr-1 h-3.5 w-3.5" /> Szabad tétel
          </Button>
        </div>
        {removable && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-500/10"
            onClick={onRemove}
            data-testid={`item-${index}-remove`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Row 1: product/name (full width) */}
      {isFree ? (
        <div className="mb-2">
          <Label className="text-[11px]">Megnevezés</Label>
          <Input
            value={item.product_name}
            onChange={(e) => onChange({ product_name: e.target.value })}
            placeholder="Pl. Friss zöldség mix"
            className="h-9"
            data-testid={`item-${index}-name`}
          />
        </div>
      ) : (
        <div className="mb-2">
          <Label className="text-[11px]">Termék</Label>
          <Select
            value={item.product_id || ""}
            onValueChange={(v) => onPickProduct(v)}
          >
            <SelectTrigger className="h-9" data-testid={`item-${index}-product`}>
              <SelectValue placeholder="Válassz terméket…" />
            </SelectTrigger>
            <SelectContent>
              {products.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                  Nincs termék rögzítve.
                </div>
              ) : (
                products.map((p) => (
                  <SelectItem key={p._id} value={p._id}>
                    {p.name}
                    {p.unit ? ` · ${p.unit}` : ""}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Row 2: qty, unit, net, vat, line total */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-24 shrink-0">
          <Label className="text-[11px]">Mennyiség</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={item.quantity}
            onChange={(e) => onChange({ quantity: e.target.value })}
            className="h-9"
            data-testid={`item-${index}-qty`}
          />
        </div>
        <div className="w-20 shrink-0">
          <Label className="text-[11px]">Egység</Label>
          <Input
            value={item.unit || ""}
            onChange={(e) => onChange({ unit: e.target.value })}
            placeholder="kg"
            className="h-9"
            data-testid={`item-${index}-unit`}
          />
        </div>
        <div className="w-32 shrink-0">
          <Label className="text-[11px]">Nettó egys. ár</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={item.net_price}
            onChange={(e) => onChange({ net_price: e.target.value })}
            className="h-9"
            data-testid={`item-${index}-net`}
          />
        </div>
        <div className="w-20 shrink-0">
          <Label className="text-[11px]">ÁFA %</Label>
          <Input
            type="number"
            min="0"
            max="100"
            step="1"
            value={item.vat_rate}
            onChange={(e) => onChange({ vat_rate: e.target.value })}
            className="h-9"
            data-testid={`item-${index}-vat`}
          />
        </div>
        <div className="ml-auto flex flex-col items-end">
          <Label className="text-[11px] text-muted-foreground">Sor nettó</Label>
          <div className="flex h-9 items-center justify-end whitespace-nowrap text-sm font-semibold tabular-nums">
            {formatFt(lineNet)}
          </div>
        </div>
      </div>
    </div>
  );
}
